import { get, divide } from 'lodash';
import {
  getFormValues,
  getFormInitialValues,
  isDirty,
  isValid,
  hasSubmitSucceeded,
} from 'redux-form';
import { createSelector } from 'reselect';

import { makeOptionsWithNull } from 'Common/FormHelper';
import { createMap } from 'Common/Helpers/format';
import {
  ING_PURCHASING_FORM_NAME,
  ING_MEASUREMENTS_FORM_NAME,
  EDIT_INGREDIENT_FORM_NAME,
} from 'Common/FormEnum';
import {
  IngMeasurementsFieldEnum,
  IngPurchasingFieldEnum,
  EditIngredientFieldEnum,
} from 'Common/FieldEnum';
import { ActiveStatusEnum } from 'Common/Constants/Enumeration/CommonEnum';
import {
  convertToGrams,
  isMeasurementComplete,
} from 'Pages/manageingredient/helpers/ingredient';
import { USDA_SOURCES } from 'Pages/manageingredient/helpers/Constants';

const root = (state) => get(state, 'ManageIngredientState.editIngredient');

export const getFetching = (state) => get(root(state), 'fetching');

export const getFetchError = (state) => get(root(state), 'fetchError', null);

export const getEditIngredientValues = (state) =>
  get(root(state), 'editIngredientValues');

export const getIngredient = (state) => get(root(state), 'ingredient');

export const getSaveSuccessful = (state) => get(root(state), 'saveSuccessful');

export const getConvertSuccessful = (state) =>
  get(root(state), 'convertSuccessful');

export const getIngredientSources = (state) =>
  get(root(state), 'ingredientSources', []);

export const getIngredientGroups = (state) =>
  get(root(state), 'ingredientGroups', []);

export const getIngredientSourcesMap = createSelector(
  getIngredientSources,
  (ingredientSources) => createMap(ingredientSources, 'ShortDesc')
);

export const getIngredientSourcesOptions = createSelector(
  getIngredientSourcesMap,
  (ingredientSourcesMap) => {
    const ingredientSources = Object.values(ingredientSourcesMap);
    const normalizedSources = ingredientSources.map((source) => ({
      Id: source.ShortDesc,
      Description: source.ShortDesc,
    }));

    return makeOptionsWithNull(normalizedSources);
  }
);

export const getSaveableIngredientSourcesOptions = createSelector(
  getIngredientSourcesMap,
  (ingredientSourcesMap) => {
    const ingredientSources = Object.values(ingredientSourcesMap);
    const saveableSources = ingredientSources
      .filter((source) => !source.ReadOnly)
      .map((source) => ({
        Id: source.ShortDesc,
        Description: source.ShortDesc,
      }));

    return makeOptionsWithNull(saveableSources);
  }
);

export const getIngredientGroupsOptions = createSelector(
  getIngredientGroups,
  (ingredientGroups) => makeOptionsWithNull(ingredientGroups)
);

export const getFetchingGroups = (state) => get(root(state), 'fetchingGroups');

export const getFetchingSources = (state) =>
  get(root(state), 'fetchingSources');

export const getIngredientSourceId = (state, sourceDesc) => {
  const sourcesMap = getIngredientSourcesMap(state);

  if (!sourcesMap[sourceDesc]) {
    return null;
  }

  return sourcesMap[sourceDesc].Id;
};

export const getEditIngredientFormValues = getFormValues(
  EDIT_INGREDIENT_FORM_NAME
);

export const getEditIngredientInitialFormValues = getFormInitialValues(
  EDIT_INGREDIENT_FORM_NAME
);

const purchasingFormValuesSelector = getFormValues(ING_PURCHASING_FORM_NAME);

export const getPurchasingFormValues = (state) => {
  const values = purchasingFormValuesSelector(state);

  const formValues = { ...values };
  formValues[IngPurchasingFieldEnum.PrimaryPrice] =
    parseFloat(formValues[IngPurchasingFieldEnum.PrimaryPrice]) || null;
  formValues[IngPurchasingFieldEnum.SecondaryUnitWeight] =
    parseFloat(formValues[IngPurchasingFieldEnum.SecondaryUnitWeight]) || null;

  return formValues;
};

export const getPurchasingFormValuesForSaving = (state) =>
  getPurchasingFormValues(state);

export const getMeasurementsFormValues = getFormValues(
  ING_MEASUREMENTS_FORM_NAME
);

export const getIngredientMeasuresAndCostFormValues = (state) => {
  const purchasingValues = getPurchasingFormValues(state);
  const measures = getFormMeasures(state).map((measure) => ({
    ...measure,
    ActiveStatus: ActiveStatusEnum.Active,
  }));
  const formValues = {
    ...purchasingValues,
    [IngMeasurementsFieldEnum.Measures]: measures,
  };

  delete formValues[IngPurchasingFieldEnum.InvItem];

  return formValues;
};

export const getFormMeasures = (state) => {
  const measurementsValues = getMeasurementsFormValues(state) || {};

  return get(measurementsValues, IngMeasurementsFieldEnum.Measures, []);
};

export const getCostPerGram = (state) => {
  const values = getPurchasingFormValues(state) || {};
  const weight = values[IngPurchasingFieldEnum.SecondaryUnitWeight];
  const qtyPerUnit = values[IngPurchasingFieldEnum.QtyPerUnit];
  const unit = values[IngPurchasingFieldEnum.SecondaryUnitMeasure];
  const weightInGrams = convertToGrams(weight, unit);
  const primaryPrice = getPrimaryPrice(state);

  if (qtyPerUnit && weightInGrams) {
    const costPerGram = divide(divide(primaryPrice, qtyPerUnit), weightInGrams);
    return costPerGram;
  }

  return 0;
};

export const getRecipesWithIngredient = (state) =>
  get(root(state), 'recipesWithIngredient');

export const getDeleteSuccessful = (state) =>
  get(root(state), 'deleteSuccessful');

export const getPurchasingUnit = (state) => {
  const values = getPurchasingFormValues(state) || {};
  const primaryPrice = getPrimaryPrice(state);
  const unit = values[IngPurchasingFieldEnum.Unit];

  if (!unit) {
    return null;
  }

  return `$${primaryPrice} PER ${unit}`;
};

export const getPrimaryPrice = (state) => {
  const values = getPurchasingFormValues(state) || {};
  return values[IngPurchasingFieldEnum.PrimaryPrice] || 0;
};

export const getPackSize = (state) => {
  const values = getPurchasingFormValues(state) || {};
  const qtyPerUnit = values[IngPurchasingFieldEnum.QtyPerUnit];
  const secondaryUnit = values[IngPurchasingFieldEnum.SecondaryUnit];

  if (!qtyPerUnit || !secondaryUnit) {
    return null;
  }
  return `${qtyPerUnit} ${secondaryUnit}`;
};

export const getPackWeight = (state) => {
  const values = getPurchasingFormValues(state) || {};
  const secondaryUnitWeight =
    values[IngPurchasingFieldEnum.SecondaryUnitWeight];
  const secondaryUnitMeasure =
    values[IngPurchasingFieldEnum.SecondaryUnitMeasure];

  if (!secondaryUnitWeight || !secondaryUnitMeasure) {
    return null;
  }
  return `${secondaryUnitWeight} ${secondaryUnitMeasure}`;
};

export const getIsReadOnly = (state) =>
  get(getIngredient(state), EditIngredientFieldEnum.IsReadOnly, false);

export const getIsSourceReadOnly = (state) => {
  const ingredientSourcesMap = getIngredientSourcesMap(state);
  const initialValues = getEditIngredientInitialFormValues(state) || {};

  const initialSourceDesc = initialValues[EditIngredientFieldEnum.SourceDesc];

  return (
    initialSourceDesc &&
    ingredientSourcesMap[initialSourceDesc] &&
    ingredientSourcesMap[initialSourceDesc].ReadOnly
  );
};

export const getIsUSDAIngredient = (state) => {
  const initialValues = getEditIngredientInitialFormValues(state) || {};

  return Object.values(USDA_SOURCES).includes(
    initialValues[EditIngredientFieldEnum.SourceDesc]
  );
};

export const getIngredientName = (state) => {
  const formValues = getEditIngredientFormValues(state);

  return get(formValues, EditIngredientFieldEnum.Description, '');
};

export const getMeasuresToDisplay = createSelector(
  getFormMeasures,
  (measures) => {
    return measures.filter(isMeasurementComplete);
  }
);

export const isDirtySelector = isDirty(EDIT_INGREDIENT_FORM_NAME);
export const isValidSelector = isValid(EDIT_INGREDIENT_FORM_NAME);
export const hasSubmitSucceededSelector = hasSubmitSucceeded(
  EDIT_INGREDIENT_FORM_NAME
);



// WEBPACK FOOTER //
// ./src/pages/manageingredient/state/editingredient/selectors.js